/**
 * Response Builder Workspace Repository
 * 
 * Handles rb_workspaces collection operations.
 * Per PHASE_B_WORK_ORDERS.md B6 - workspace bootstrap.
 * Per Δ-B3: Idempotent with deterministic IDs and concurrency protection.
 */

import { 
  collection, 
  doc, 
  getDoc, 
  query, 
  where, 
  getDocs,
  runTransaction,
  Timestamp,
  type Firestore 
} from 'firebase/firestore';

export interface Workspace {
  id: string;
  name: string;
  createdByUserId: string;
  createdAt: Timestamp;
}

export interface Membership {
  id: string;
  workspaceId: string;
  userId: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  createdAt: Timestamp;
}

/**
 * Get workspace by ID
 */
export async function getWorkspace(
  db: Firestore,
  workspaceId: string
): Promise<Workspace | null> {
  const workspaceRef = doc(db, 'rb_workspaces', workspaceId);
  const workspaceSnap = await getDoc(workspaceRef);
  
  if (!workspaceSnap.exists()) {
    return null;
  }
  
  return {
    id: workspaceSnap.id,
    ...workspaceSnap.data(),
  } as Workspace;
}

/**
 * Get user's default workspace
 * Returns the first workspace where user is a member
 */
export async function getUserDefaultWorkspace(
  db: Firestore,
  userId: string
): Promise<Workspace | null> {
  // Query memberships for this user
  const membershipsRef = collection(db, 'rb_memberships');
  const q = query(membershipsRef, where('userId', '==', userId));
  const membershipsSnap = await getDocs(q);
  
  if (membershipsSnap.empty) {
    return null;
  }
  
  // Get first membership's workspace
  const firstMembership = membershipsSnap.docs[0].data() as Membership;
  
  // Δ-B3: Fail loudly if membership exists without workspace
  const workspace = await getWorkspace(db, firstMembership.workspaceId);
  if (!workspace) {
    throw new Error(
      `[Bootstrap Safety] Orphaned membership detected: membership exists for workspace ${firstMembership.workspaceId} but workspace does not exist. UserId: ${userId}`
    );
  }
  
  return workspace;
}

/**
 * Create workspace and owner membership using a transaction.
 * Per Δ-B3: Uses deterministic IDs (userId-based) for idempotency.
 * 
 * INVARIANT: At most one workspace per user in Phase B.
 * Transaction ensures atomicity - either both docs created or neither.
 */
export async function createWorkspaceWithOwner(
  db: Firestore,
  userId: string
): Promise<{ workspace: Workspace; membership: Membership }> {
  // Δ-B3: Deterministic IDs for idempotency
  // If concurrent calls happen, they will use the same IDs
  const workspaceId = `ws_${userId}`;
  const membershipId = `mbr_${userId}_${workspaceId}`;
  
  const workspaceRef = doc(db, 'rb_workspaces', workspaceId);
  const membershipRef = doc(db, 'rb_memberships', membershipId);
  
  // Use transaction for atomicity
  const result = await runTransaction(db, async (transaction) => {
    // Check if workspace already exists
    const workspaceSnap = await transaction.get(workspaceRef);
    const membershipSnap = await transaction.get(membershipRef);
    
    // Δ-B3: Fail loudly on partial state
    if (workspaceSnap.exists() && !membershipSnap.exists()) {
      throw new Error(
        `[Bootstrap Safety] Inconsistent state: workspace exists but membership missing for userId: ${userId}`
      );
    }
    if (!workspaceSnap.exists() && membershipSnap.exists()) {
      throw new Error(
        `[Bootstrap Safety] Inconsistent state: membership exists but workspace missing for userId: ${userId}`
      );
    }
    
    // If both exist, return existing (idempotent)
    if (workspaceSnap.exists() && membershipSnap.exists()) {
      return {
        workspace: {
          id: workspaceSnap.id,
          ...workspaceSnap.data(),
        } as Workspace,
        membership: {
          id: membershipSnap.id,
          ...membershipSnap.data(),
        } as Membership,
      };
    }
    
    // Create both in transaction
    const now = Timestamp.now();
    
    const workspace: Workspace = {
      id: workspaceId,
      name: 'Default Workspace',
      createdByUserId: userId,
      createdAt: now,
    };
    
    const membership: Membership = {
      id: membershipId,
      workspaceId,
      userId,
      role: 'owner',
      createdAt: now,
    };
    
    transaction.set(workspaceRef, {
      name: workspace.name,
      createdByUserId: workspace.createdByUserId,
      createdAt: workspace.createdAt,
    });
    
    transaction.set(membershipRef, {
      workspaceId: membership.workspaceId,
      userId: membership.userId,
      role: membership.role,
      createdAt: membership.createdAt,
    });
    
    return { workspace, membership };
  });
  
  return result;
}

/**
 * Bootstrap workspace for user on first login.
 * 
 * Δ-B3 INVARIANT: At most one workspace is created per user in Phase B.
 * 
 * Safety guarantees:
 * - Idempotent: Concurrent calls use same deterministic IDs
 * - Transactional: Workspace + membership created atomically
 * - Fail-loud: Throws on partial/inconsistent states
 * - No race conditions: Transaction retry handles concurrency
 */
export async function bootstrapWorkspace(
  db: Firestore,
  userId: string
): Promise<Workspace> {
  // Check if user already has a workspace
  const existingWorkspace = await getUserDefaultWorkspace(db, userId);
  
  if (existingWorkspace) {
    return existingWorkspace;
  }
  
  // Create new workspace (idempotent, transactional)
  const { workspace } = await createWorkspaceWithOwner(db, userId);
  return workspace;
}
